import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import Navbar from "components/Navbars";
import PricingSection from "components/sections/PricingSection";
import API_BASE_URL from "config/api";

function decodeUrlSafeBase64(value) {
  if (!value || typeof value !== "string") return "";

  try {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const binary = atob(padded);
    return decodeURIComponent(
      Array.from(binary)
        .map((ch) => `%${ch.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
  } catch (e) {
    return "";
  }
}

function formatExpireText(rawExpire, notActivatedText) {
  if (!rawExpire) return notActivatedText;

  const date = new Date(rawExpire);
  if (Number.isNaN(date.getTime()) || date.getFullYear() < 2000) {
    return notActivatedText;
  }

  return date.toLocaleString();
}

function getExpireFromProfile(payload) {
  if (!payload || typeof payload !== "object") return "";
  return (
    payload.level_expire_time ||
    payload.expire_at ||
    payload.expire_time ||
    payload.plan_expire_at ||
    payload.plan_expire_time ||
    ""
  );
}

function ManageAccount() {
  const { t } = useTranslation();
  const { base64urlsafe_username: encodedUsername } = useParams();
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [userFound, setUserFound] = useState(true);
  const [targetUserPk, setTargetUserPk] = useState(null);
  const [levelExpireTime, setLevelExpireTime] = useState(null);
  const [profileError, setProfileError] = useState("");

  const decodedUsername = useMemo(
    () => decodeUrlSafeBase64(encodedUsername || ""),
    [encodedUsername]
  );
  const hasValidExpire = useMemo(() => {
    if (!levelExpireTime) return false;
    const d = new Date(levelExpireTime);
    return !Number.isNaN(d.getTime()) && d.getFullYear() >= 2000;
  }, [levelExpireTime]);

  useEffect(() => {
    if (!decodedUsername) return;

    let cancelled = false;

    const fetchProfile = async () => {
      setLoadingProfile(true);
      setUserFound(true);
      setTargetUserPk(null);
      setLevelExpireTime(null);
      setProfileError("");

      try {
        const res = await fetch(`${API_BASE_URL}/web2/payment/topup/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ username: decodedUsername }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data?.ok) {
          if (!cancelled) {
            if (res.status === 404 || data?.code === "USER_NOT_FOUND") {
              setUserFound(false);
              setProfileError(data?.detail || "没有这个用户");
            } else {
              setProfileError(data?.detail || "未能获取该用户信息，请稍后重试。");
            }
          }
          return;
        }

        if (!cancelled) {
          setTargetUserPk(data?.user_pk ?? null);
          setLevelExpireTime(getExpireFromProfile(data) || null);
        }
      } catch (e) {
        if (!cancelled) {
          setProfileError("网络异常，未能获取该用户信息，请稍后重试。");
        }
      } finally {
        if (!cancelled) {
          setLoadingProfile(false);
        }
      }
    };

    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [decodedUsername]);

  return (
    <>
      <Navbar
        transparent
        light
        showMenu={false}
        action={{
          type: "internal",
          route: "/",
          label: t("nav.home"),
          color: "info",
        }}
      />
      <MKBox
        minHeight="20vh"
        width="100%"
        sx={{
          position: "relative",
          backgroundImage: "linear-gradient(195deg, rgba(66,66,74,0.9), rgba(25,25,25,0.9))",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      />
      <Card
        sx={{
          p: { xs: 2, md: 3 },
          width: "100%",
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, md: 3 },
          mt: -6,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {!decodedUsername ? (
          <Alert severity="error">{t("manageAccount.invalidLink")}</Alert>
        ) : (
          <>
            <Card
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid",
                borderColor: "info.light",
              }}
            >
              <MKTypography variant="h5" fontWeight="bold" mb={1}>
                {t("manageAccount.topupFor")}
              </MKTypography>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <MKTypography variant="button" color="text" display="block">
                    {t("manageAccount.username")}
                  </MKTypography>
                  <MKTypography variant="h6" fontWeight="bold">
                    {decodedUsername}
                  </MKTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MKTypography variant="button" color="text" display="block">
                    {t("manageAccount.currentExpire")}
                  </MKTypography>
                  <MKTypography variant="h6" fontWeight="bold">
                    {loadingProfile
                      ? t("manageAccount.loading")
                      : formatExpireText(levelExpireTime, t("manageAccount.notActivated"))}
                  </MKTypography>
                </Grid>
              </Grid>
              {profileError ? (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  {profileError}
                </Alert>
              ) : null}
            </Card>

            {userFound && !loadingProfile ? (
              <PricingSection
                userName={decodedUsername}
                expireAt={hasValidExpire ? levelExpireTime : null}
                hasEverPurchased={hasValidExpire}
                isLoggedIn
                isUserInfo
                paymentRoute="/payment/public/"
                targetUserPk={targetUserPk}
                returnTo={`/manage-account/${encodedUsername}/#pricing`}
              />
            ) : null}
          </>
        )}
      </Card>
    </>
  );
}

export default ManageAccount;
